import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Search,
  Hotel,
  PersonAdd,
  ExitToApp,
  LocalHospital,
  KingBed,
  Person,
  People,
  EscalatorWarning,
  Print
} from '@mui/icons-material';
import { patientAPI, ipdAPI } from '../services/api';
import IPDAdmissionForms from './IPDAdmissionForms';

function IPDAdmission({ preselectedPatient, onSuccess }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [plannedDays, setPlannedDays] = useState('');
  const [occupancyType, setOccupancyType] = useState('single');
  const [activeAdmissions, setActiveAdmissions] = useState([]);
  const [showAdmissionForm, setShowAdmissionForm] = useState(false);
  const [clinicalInfo, setClinicalInfo] = useState({
    chiefComplaint: '', diagnosis: '', pastMedicalHistory: '',
    currentMedications: '', familyHistory: '', doctorNotes: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForms, setShowForms] = useState(false);

  useEffect(() => {
    loadAvailableRooms();
    loadActiveAdmissions();

    // If preselectedPatient is provided, auto-select it and skip patient search
    if (preselectedPatient) {
      // Convert snake_case to camelCase for consistency with internal patient data format
      const normalizedPatient = {
        id: preselectedPatient.id,
        patientCode: preselectedPatient.patient_code || preselectedPatient.patientCode,
        firstName: preselectedPatient.first_name || preselectedPatient.firstName,
        lastName: preselectedPatient.last_name || preselectedPatient.lastName,
        phone: preselectedPatient.phone,
        gender: preselectedPatient.gender,
        dateOfBirth: preselectedPatient.date_of_birth || preselectedPatient.dateOfBirth,
        email: preselectedPatient.email,
        address: preselectedPatient.address,
        chiefComplaint: preselectedPatient.chief_complaint || preselectedPatient.chiefComplaint || '',
        diagnosis: preselectedPatient.diagnosis || '',
        pastMedicalHistory: preselectedPatient.past_medical_history || preselectedPatient.pastMedicalHistory || '',
        currentMedications: preselectedPatient.current_medications || preselectedPatient.currentMedications || '',
        familyHistory: preselectedPatient.family_history || preselectedPatient.familyHistory || '',
        doctorNotes: preselectedPatient.doctor_notes || preselectedPatient.doctorNotes || ''
      };
      handleSelectPatient(normalizedPatient);
    } else {
      loadAllPatients();
    }
  }, [preselectedPatient]);

  const loadAvailableRooms = async () => {
    try {
      const response = await ipdAPI.getAvailableRooms();
      setRooms(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load rooms:', err);
    }
  };

  const loadActiveAdmissions = async () => {
    try {
      const response = await ipdAPI.getActiveAdmissions();
      setActiveAdmissions(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load admissions:', err);
    }
  };

  const loadAllPatients = async () => {
    setLoading(true);
    try {
      const response = await patientAPI.search('');
      const data = response.data.data || response.data;
      // Sort by ID descending (newest first)
      const sortedData = Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : [];
      setPatients(sortedData);
    } catch (err) {
      console.error('Failed to load patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadAllPatients();
      return;
    }

    setLoading(true);
    try {
      const response = await patientAPI.search(searchQuery);
      const data = response.data.data || response.data;
      // Sort by ID descending (newest first)
      const sortedData = Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : [];
      setPatients(sortedData);
    } catch (err) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setClinicalInfo({
      chiefComplaint:     patient.chief_complaint    || patient.chiefComplaint    || '',
      diagnosis:          patient.diagnosis          || '',
      pastMedicalHistory: patient.past_medical_history || patient.pastMedicalHistory || '',
      currentMedications: patient.current_medications || patient.currentMedications || '',
      familyHistory:      patient.family_history     || patient.familyHistory     || '',
      doctorNotes:        patient.doctor_notes       || patient.doctorNotes       || ''
    });
    setShowAdmissionForm(true);
    setError('');
    setSuccess('');
  };

  const handleAdmit = async (e) => {
    e.preventDefault();

    if (!selectedRoom) {
      setError('Please select a room');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await ipdAPI.admitPatient({
        patientId: selectedPatient.id,
        roomId: selectedRoom.id,
        plannedDays: plannedDays ? parseInt(plannedDays) : 0,
        occupancyType,
        chiefComplaint:     clinicalInfo.chiefComplaint     || undefined,
        diagnosis:          clinicalInfo.diagnosis          || undefined,
        pastMedicalHistory: clinicalInfo.pastMedicalHistory || undefined,
        currentMedications: clinicalInfo.currentMedications || undefined,
        familyHistory:      clinicalInfo.familyHistory      || undefined,
        doctorNotes:        clinicalInfo.doctorNotes        || undefined
      });

      const data = response.data.data || response.data;
      const billMsg = data.billNumber ? ` Invoice ${data.billNumber} generated.` : '';
      setSuccess(`Patient admitted successfully! Room: ${data.roomNumber}, IPD ID: ${data.ipdId}, Daily Charge: ₹${data.dailyCharge}.${billMsg}`);

      setTimeout(() => {
        if (onSuccess) {
          // If using preselected patient (from dialog), call parent callback
          onSuccess();
        } else {
          // Standalone mode - reset form
          setShowAdmissionForm(false);
          setSelectedPatient(null);
          setSelectedRoom(null);
          setPlannedDays('');
          setOccupancyType('single');
          setClinicalInfo({ chiefComplaint: '', diagnosis: '', pastMedicalHistory: '', currentMedications: '', familyHistory: '', doctorNotes: '' });
          setSearchQuery('');
          setPatients([]);
          loadAvailableRooms();
          loadActiveAdmissions();
          setSuccess('');
        }
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Admission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDischarge = async (admissionId) => {
    if (!window.confirm('Are you sure you want to discharge this patient?')) {
      return;
    }

    try {
      await ipdAPI.dischargePatient(admissionId);
      setSuccess('Patient discharged successfully!');
      loadAvailableRooms();
      loadActiveAdmissions();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Discharge failed. Please try again.');
    }
  };

  const getRoomChargeForOccupancy = (room, type) => {
    if (type === 'with_attendant') return room.second_patient_charge;
    if (type === 'double') return room.third_patient_charge;
    return room.first_patient_charge; // single
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'room': return 'success';
      case 'cottage': return 'primary';
      case 'hotel': return 'success';
      case 'ward': return 'info';
      default: return 'default';
    }
  };

  const content = (
    <>
      {!preselectedPatient && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Hotel sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            IPD Admission
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {!preselectedPatient && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Active Admissions</Typography>
          {activeAdmissions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography color="text.secondary">
                No active admissions
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Patient Code</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Room</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Admission Date</strong></TableCell>
                    <TableCell align="right"><strong>Days</strong></TableCell>
                    <TableCell align="right"><strong>Planned</strong></TableCell>
                    <TableCell align="right"><strong>Daily Charge</strong></TableCell>
                    <TableCell align="right"><strong>Total Charges</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeAdmissions.map((admission) => (
                    <TableRow key={admission.id} hover>
                      <TableCell>{admission.patient_code}</TableCell>
                      <TableCell>{admission.first_name} {admission.last_name}</TableCell>
                      <TableCell>{admission.room_number}</TableCell>
                      <TableCell>
                        <Chip
                          label={admission.category_name}
                          color={getCategoryColor(admission.category_name)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{new Date(admission.admission_date).toLocaleDateString()}</TableCell>
                      <TableCell align="right">{admission.days_admitted}</TableCell>
                      <TableCell align="right">{admission.planned_days || 0}</TableCell>
                      <TableCell align="right">₹{admission.daily_charge}</TableCell>
                      <TableCell align="right">₹{admission.total_charges}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Print />}
                            onClick={() => {
                              setSelectedPatient({
                                id: admission.patient_id,
                                firstName: admission.first_name,
                                lastName: admission.last_name,
                                patientCode: admission.patient_code,
                                phone: admission.phone,
                                gender: admission.gender,
                                address: admission.address,
                                admissionId: admission.id,
                                admissionDate: admission.admission_date,
                                roomNumber: admission.room_number,
                                chiefComplaint: admission.chief_complaint || '',
                                diagnosis: admission.diagnosis || '',
                                pastMedicalHistory: admission.past_medical_history || '',
                                currentMedications: admission.current_medications || '',
                                familyHistory: admission.family_history || '',
                                doctorNotes: admission.doctor_notes || '',
                              });
                              setShowForms(true);
                            }}
                          >
                            Forms
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<ExitToApp />}
                            onClick={() => handleDischarge(admission.id)}
                          >
                            Discharge
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {!preselectedPatient && <Divider sx={{ my: 3 }} />}

      {!showAdmissionForm && !preselectedPatient ? (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>New Admission</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search by patient code, name, or phone"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
                sx={{ minWidth: 120 }}
              >
                Search
              </Button>
            </Box>

            {patients.length > 0 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Patient Code</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Gender</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patients.map((patient) => (
                      <TableRow key={patient.id} hover>
                        <TableCell>{patient.patientCode}</TableCell>
                        <TableCell>{patient.firstName} {patient.lastName}</TableCell>
                        <TableCell>{patient.phone}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{patient.gender}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PersonAdd />}
                            onClick={() => handleSelectPatient(patient)}
                          >
                            Admit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}

        {showAdmissionForm && (
          <form onSubmit={handleAdmit}>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.main' }}>
              <Typography variant="h6" color="primary">
                Patient: {selectedPatient.firstName} {selectedPatient.lastName}
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Planned Days"
                  value={plannedDays}
                  onChange={(e) => setPlannedDays(e.target.value)}
                  placeholder="Enter planned days (optional)"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Occupancy Type
                </Typography>
                <ToggleButtonGroup
                  value={occupancyType}
                  exclusive
                  onChange={(e, val) => val && setOccupancyType(val)}
                  size="medium"
                >
                  <ToggleButton value="single" sx={{ px: 3, gap: 1 }}>
                    <Person fontSize="small" />
                    Single Patient
                  </ToggleButton>
                  <ToggleButton value="with_attendant" sx={{ px: 3, gap: 1 }}>
                    <EscalatorWarning fontSize="small" />
                    With Attendant
                  </ToggleButton>
                  <ToggleButton value="double" sx={{ px: 3, gap: 1 }}>
                    <People fontSize="small" />
                    2 Patients
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalHospital sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">Clinical Information</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Chief Complaint"
                    value={clinicalInfo.chiefComplaint}
                    onChange={(e) => setClinicalInfo(p => ({ ...p, chiefComplaint: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Diagnosis"
                    value={clinicalInfo.diagnosis}
                    onChange={(e) => setClinicalInfo(p => ({ ...p, diagnosis: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Past Medical History"
                    value={clinicalInfo.pastMedicalHistory}
                    onChange={(e) => setClinicalInfo(p => ({ ...p, pastMedicalHistory: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Current Medications"
                    value={clinicalInfo.currentMedications}
                    onChange={(e) => setClinicalInfo(p => ({ ...p, currentMedications: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Family History"
                    value={clinicalInfo.familyHistory}
                    onChange={(e) => setClinicalInfo(p => ({ ...p, familyHistory: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Doctor Notes"
                    value={clinicalInfo.doctorNotes}
                    onChange={(e) => setClinicalInfo(p => ({ ...p, doctorNotes: e.target.value }))} />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <KingBed sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">Select Room</Typography>
              </Box>
              <Grid container spacing={2}>
                {rooms.map((room) => (
                  <Grid item xs={12} md={6} key={room.id}>
                    <Paper
                      onClick={() => setSelectedRoom(room)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: 2,
                        borderColor: selectedRoom?.id === room.id ? 'primary.main' : 'divider',
                        bgcolor: selectedRoom?.id === room.id ? 'primary.50' : 'white',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            Room {room.room_number}
                          </Typography>
                          <Chip
                            label={room.category_name.toUpperCase()}
                            color={getCategoryColor(room.category_name)}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Occupancy: {room.current_occupancy}/{room.max_capacity}
                          </Typography>
                          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                            ₹{getRoomChargeForOccupancy(room, occupancyType)}/day
                          </Typography>
                        </Box>
                        {room.current_occupancy >= room.max_capacity && (
                          <Chip label="Full" color="error" size="small" />
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !selectedRoom}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
                fullWidth
              >
                {loading ? 'Admitting...' : 'Admit Patient'}
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                size="large"
                startIcon={<Print />}
                onClick={() => setShowForms(true)}
                sx={{ minWidth: 160 }}
              >
                Print Forms
              </Button>
              <Button
                type="button"
                variant="outlined"
                size="large"
                onClick={() => {
                  setShowAdmissionForm(false);
                  setSelectedPatient(null);
                  setSelectedRoom(null);
                  setPlannedDays('');
                  setOccupancyType('single');
                  setClinicalInfo({ chiefComplaint: '', diagnosis: '', pastMedicalHistory: '', currentMedications: '', familyHistory: '', doctorNotes: '' });
                }}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        )}
      {showForms && selectedPatient && (
        <IPDAdmissionForms
          patient={selectedPatient}
          admission={selectedPatient.admissionId ? {
            admissionId: selectedPatient.admissionId,
            roomNumber: selectedPatient.roomNumber || selectedRoom?.room_number || '',
            admissionDate: selectedPatient.admissionDate || new Date().toISOString(),
            chiefComplaint: selectedPatient.chiefComplaint || '',
            diagnosis: selectedPatient.diagnosis || '',
            pastMedicalHistory: selectedPatient.pastMedicalHistory || '',
            currentMedications: selectedPatient.currentMedications || '',
            familyHistory: selectedPatient.familyHistory || '',
            doctorNotes: selectedPatient.doctorNotes || '',
          } : {
            roomNumber: selectedRoom?.room_number || '',
            admissionDate: new Date().toISOString()
          }}
          onClose={() => setShowForms(false)}
        />
      )}
    </>
  );

  // If used in dialog (with preselectedPatient), return content without Card wrapper
  if (preselectedPatient) {
    return content;
  }

  // If standalone mode, wrap in Card
  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        {content}
      </CardContent>
    </Card>
  );
}

export default IPDAdmission;
